import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Loader2, Youtube, Twitch, Swords, Trophy, Skull, Heart, Ghost } from "lucide-react";

const kindLabel = { video: "Video Idea", challenge: "Challenge", build: "Build" };

export default function PublicProfile() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/profile/${username}`).then(({ data }) => setData(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [username]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (notFound) return <div className="py-32 text-center"><Ghost className="mx-auto h-10 w-10 text-primary" /><p className="mt-4 font-display text-3xl tracking-wider">Lost in the fog</p><p className="text-foreground/50">This creator doesn't exist.</p></div>;

  const { profile, saved, posts } = data;

  return (
    <div data-testid="public-profile-page" className="mx-auto max-w-4xl px-6 py-14 md:px-10">
      <div className="border border-border bg-card p-8">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex h-20 w-20 items-center justify-center border border-primary/40 bg-primary/10">
            {profile.avatar ? <img src={profile.avatar} alt={profile.username} className="h-full w-full object-cover" /> : <Skull className="h-9 w-9 text-primary" />}
          </div>
          <div>
            <h1 className="font-display text-5xl tracking-wider">{profile.username}</h1>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">{profile.rank}</p>
          </div>
        </div>

        {profile.bio && <p className="mt-5 text-foreground/70">{profile.bio}</p>}

        <div className="mt-5 flex flex-wrap gap-3">
          {profile.youtube && <a href={profile.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-primary/50 hover:text-primary"><Youtube className="h-4 w-4" /> YouTube</a>}
          {profile.twitch && <a href={profile.twitch} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-primary/50 hover:text-primary"><Twitch className="h-4 w-4" /> Twitch</a>}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat icon={Trophy} label="Score" value={profile.score} />
          <Stat icon={Swords} label="Challenges" value={profile.challenges_completed} />
          <Field label="Main Killer" value={profile.favorite_killer || "—"} />
          <Field label="Main Survivor" value={profile.favorite_survivor || "—"} />
        </div>
      </div>

      {saved.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-3xl tracking-wide">Shared Ideas</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {saved.map((item) => (
              <Link key={item.id} to={`/share/${item.id}`} className="border border-border bg-card p-5 transition-colors hover:border-primary/40">
                <span className="border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">{kindLabel[item.kind]}</span>
                <h3 className="mt-2 font-display text-xl tracking-wide">{item.data.title || item.data.build_name || "Untitled"}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {posts.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-3xl tracking-wide">Community Posts</h2>
          <div className="mt-4 space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary">{p.type}</span>
                  <span className="flex items-center gap-1.5 text-sm text-foreground/50"><Heart className="h-4 w-4" />{p.likes}</span>
                </div>
                <h3 className="mt-2 font-display text-xl tracking-wide">{p.title}</h3>
                {p.image_path && <img src={`${API}/files/${p.image_path}`} alt={p.title} loading="lazy" className="mt-2 max-h-72 w-full max-w-xs border border-white/10 object-cover" />}
                <p className="mt-1 line-clamp-2 text-sm text-foreground/60">{p.content}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border bg-background/50 p-4 text-center">
    <Icon className="mx-auto h-5 w-5 text-primary" />
    <p className="mt-2 font-display text-2xl">{value}</p>
    <p className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">{label}</p>
  </div>
);
const Field = ({ label, value }) => (
  <div className="border border-background/50 bg-background/50 p-4 text-center">
    <p className="font-bold text-foreground">{value}</p>
    <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-foreground/50">{label}</p>
  </div>
);
